import React, { useCallback, useEffect, useRef, useState } from "react";

import { qlAlert } from "../../utils/dialogs";
import { openCinetPayExternally } from "../services/externalCheckout";
import {
  getTeacherWalletRechargeStatus,
  initializeTeacherWalletRecharge,
  listTeacherWalletHistory,
} from "../services/teacherApi";
import {
  RECHARGE_ACTIVE_STATES,
  RECHARGE_EXPIRATION_MS,
  RECHARGE_FAILED_STATES,
  RECHARGE_SUCCESS_STATES,
  clearWalletRechargeAttempt,
  normalizeRechargeStatus,
  persistWalletRechargeAttempt,
  readWalletRechargeAttempt,
} from "../services/walletRechargeAttempt";

const typeLabels = { payment: "Paiement", recharge: "Recharge" };
const statusLabels = { pending: "En attente", processing: "En cours", success: "Réussie", failed: "Échouée" };

const formatAmount = (amount, currency) => `${Number(amount || 0).toLocaleString("fr-FR")} ${currency}`;
const formatDate = (value) => value ? new Date(value).toLocaleString("fr-FR") : "—";

export default function TeacherWallet({ wallet, refreshKey = 0, onRefreshBalance, onBack }) {
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [history, setHistory] = useState({ count: 0, page: 1, totalPages: 0, next: null, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recharge, setRecharge] = useState({ open: false, amount: "", confirming: false, status: "IDLE", rechargeId: null, paymentUrl: null, checking: false, error: null, bonusAmount: 0 });
  const rechargeLock = useRef(false);
  const currency = wallet?.currency || "FCFA";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listTeacherWalletHistory({ page, pageSize: 10, type: type || undefined });
      setHistory(result);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Impossible de charger l’historique du portefeuille.");
    } finally {
      setLoading(false);
    }
  }, [page, type]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { onRefreshBalance?.().catch(() => {}); }, [onRefreshBalance, refreshKey]);

  const applyRechargeStatus = useCallback(async (data) => {
    const status = normalizeRechargeStatus(data);
    if (RECHARGE_SUCCESS_STATES.has(status)) {
      clearWalletRechargeAttempt();
      const bonusAmount = Number(data?.bonus_amount || 0);
      setRecharge({ open: true, amount: "", confirming: false, status: "SUCCESS", rechargeId: null, paymentUrl: null, checking: false, error: null, bonusAmount });
      if (bonusAmount > 0) {
        qlAlert(
          `Recharge réussie ! Vous avez obtenu un bonus de bienvenue de +100% : ${formatAmount(bonusAmount, currency)} offerts en plus, pour un total de ${formatAmount(bonusAmount + (Number(data?.amount) || 0), currency)}.`,
          "success",
        );
      }
      await onRefreshBalance?.();
      await load();
      return;
    }
    if (RECHARGE_FAILED_STATES.has(status)) {
      clearWalletRechargeAttempt();
      const message = status === "EXPIRED"
        ? "La tentative a expiré après 2 minutes. Vous pouvez saisir un nouveau montant."
        : "Recharge échouée. Aucun montant n'a été crédité.";
      setRecharge({ open: true, amount: "", confirming: false, status, rechargeId: null, paymentUrl: null, checking: false, error: message, bonusAmount: 0 });
      await load();
      return;
    }
    const attempt = readWalletRechargeAttempt();
    if (attempt) persistWalletRechargeAttempt({ ...attempt, status });
    setRecharge((current) => ({ ...current, status, checking: false, error: null }));
  }, [load, onRefreshBalance, currency]);

  const refreshRechargeStatus = useCallback(async () => {
    const attempt = readWalletRechargeAttempt();
    const rechargeId = recharge.rechargeId || attempt?.rechargeId;
    if (!rechargeId || recharge.checking) return;
    setRecharge((current) => ({ ...current, checking: true, error: null }));
    try {
      await applyRechargeStatus(await getTeacherWalletRechargeStatus(rechargeId));
    } catch (requestError) {
      const code = requestError.response?.status;
      const messages = { 401: "Votre session a expiré.", 403: "Cette recharge ne vous appartient pas.", 404: "Cette recharge est introuvable." };
      setRecharge((current) => ({ ...current, checking: false, error: messages[code] || "Le statut de la recharge n’a pas encore pu être confirmé." }));
    }
  }, [applyRechargeStatus, recharge.checking, recharge.rechargeId]);

  useEffect(() => {
    const attempt = readWalletRechargeAttempt();
    if (!attempt) return;
    setRecharge((current) => ({ ...current, open: true, amount: String(attempt.amount), status: attempt.status === "INITIALIZING" ? "UNCERTAIN" : attempt.status, rechargeId: attempt.rechargeId || null, paymentUrl: attempt.paymentUrl || null, error: attempt.rechargeId ? null : "L’initialisation ne peut pas être rejouée. Vérifiez ultérieurement votre historique." }));
  }, []);

  useEffect(() => {
    if (!RECHARGE_ACTIVE_STATES.has(recharge.status) || !recharge.rechargeId) return undefined;
    const timer = window.setInterval(refreshRechargeStatus, 7000);
    const attempt = readWalletRechargeAttempt();
    const elapsed = attempt?.createdAt ? Date.now() - new Date(attempt.createdAt).getTime() : 0;
    const remaining = Math.max(0, RECHARGE_EXPIRATION_MS - elapsed);
    const timeout = window.setTimeout(() => {
      refreshRechargeStatus();
    }, remaining);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(timeout);
    };
  }, [recharge.rechargeId, recharge.status, refreshRechargeStatus]);

  const amountNumber = Number(recharge.amount);
  const amountValid = /^\d+$/.test(recharge.amount) && amountNumber >= 100 && amountNumber <= 2500000;

  const abandonOrphanedRecharge = () => {
    if (recharge.rechargeId) return;
    clearWalletRechargeAttempt();
    setRecharge((current) => ({ ...current, status: "IDLE", error: null }));
  };

  const initializeRecharge = async () => {
    if (!amountValid || rechargeLock.current || recharge.rechargeId || readWalletRechargeAttempt() || RECHARGE_ACTIVE_STATES.has(recharge.status)) return;
    rechargeLock.current = true;
    const attempt = { amount: amountNumber, status: "INITIALIZING", rechargeId: null, paymentUrl: null, createdAt: new Date().toISOString() };
    persistWalletRechargeAttempt(attempt);
    setRecharge((current) => ({ ...current, confirming: false, status: "INITIALIZING", error: null }));
    try {
      const result = await initializeTeacherWalletRecharge(amountNumber);
      const rechargeId = result?.recharge_id || result?.id;
      const paymentUrl = result?.payment_url || result?.payment?.payment_url || null;
      const status = normalizeRechargeStatus(result);
      if (!rechargeId) throw new Error("RECHARGE_ID_ABSENT");
      const saved = { ...attempt, rechargeId, paymentUrl, status };
      persistWalletRechargeAttempt(saved);
      setRecharge((current) => ({ ...current, status, rechargeId, paymentUrl, error: null }));
      if (!paymentUrl) {
        setRecharge((current) => ({ ...current, error: "La recharge a été initialisée sans URL de paiement. Utilisez Actualiser le statut." }));
      } else {
        try {
          await openCinetPayExternally(paymentUrl);
        } catch {
          setRecharge((current) => ({ ...current, error: "La recharge a été initialisée, mais l’URL CinetPay a été refusée ou n’a pas pu être ouverte. Aucun nouveau POST ne sera envoyé." }));
        }
      }
      if (RECHARGE_SUCCESS_STATES.has(status) || RECHARGE_FAILED_STATES.has(status)) await applyRechargeStatus(result);
    } catch (requestError) {
      const code = requestError.response?.status;
      const detail = requestError.response?.data?.detail;
      if (requestError.response) {
        clearWalletRechargeAttempt();
        const messages = { 400: "Le montant de recharge est invalide.", 401: "Votre session a expiré.", 403: "Cette recharge n’est pas autorisée.", 409: detail || "Une recharge est déjà en cours.", 422: detail || "La recharge n’a pas pu être initialisée.", 500: "Le service de recharge est indisponible.", 502: "Le service de paiement est temporairement indisponible.", 503: "Le service de recharge est temporairement indisponible." };
        setRecharge((current) => ({ ...current, status: "FAILED", error: `${messages[code] || "L’initialisation de la recharge a échoué."} Vous pouvez réessayer.` }));
      } else {
        setRecharge((current) => ({ ...current, status: "UNCERTAIN", error: "Le résultat de l’initialisation n’a pas pu être confirmé. Aucun nouveau POST ne sera envoyé." }));
      }
    } finally {
      rechargeLock.current = false;
    }
  };

  const chooseType = (value) => {
    setType(value);
    setPage(1);
  };

  return (
    <main className="ql-wallet-main">
      <header className="ql-exam-toolbar">
        <button type="button" className="ql-exam-back" onClick={onBack}><i className="bi bi-arrow-left" /> Dashboard</button>
        <div><span className="ql-teacher-eyebrow">Finances</span><h1>Portefeuille</h1></div>
        <span className="ql-wallet-count">{history.count} transaction{history.count > 1 ? "s" : ""}</span>
      </header>

      <section className="ql-wallet-balance" aria-label="Solde du portefeuille">
        <i className="bi bi-wallet2" aria-hidden="true" />
        <div><span>Solde actuel</span><strong>{formatAmount(wallet?.balance, currency)}</strong></div>
      </section>

      {wallet?.first_recharge_bonus_available && recharge.status !== "SUCCESS" && (
        <div className="ql-wallet-bonus-banner" role="note">
          <i className="bi bi-gift-fill" aria-hidden="true" />
          <div>
            <strong>Bonus de bienvenue : +100% sur votre 1ère recharge !</strong>
            <span>Rechargez par exemple 500 {currency}, votre solde affichera 1000 {currency}. Offre valable une seule fois.</span>
          </div>
        </div>
      )}

      <section className="ql-wallet-recharge">
        <div><span className="ql-teacher-eyebrow">Crédit</span><h2>Recharger le portefeuille</h2><p>Montant autorisé : de 100 à 2 500 000 {currency}.</p></div>
        {!recharge.open && recharge.status !== "SUCCESS" && <button type="button" className="btn btn-primary" onClick={() => setRecharge((current) => ({ ...current, open: true, error: null }))}>Recharger</button>}
        {recharge.status === "SUCCESS" && (
          <div className="ql-recharge-message success" role="status">
            Recharge confirmée{recharge.bonusAmount > 0 ? ` — bonus de bienvenue de ${formatAmount(recharge.bonusAmount, currency)} offert !` : "."} Le solde et l’historique ont été actualisés.
          </div>
        )}
        {recharge.open && !recharge.confirming && (
          <div className="ql-recharge-form">
            <label htmlFor="wallet-recharge-amount">Montant à recharger</label>
            <div><input id="wallet-recharge-amount" type="number" min="100" max="2500000" step="1" value={recharge.amount} disabled={Boolean(recharge.rechargeId) || RECHARGE_ACTIVE_STATES.has(recharge.status)} onChange={(event) => setRecharge((current) => ({ ...current, amount: event.target.value, status: "IDLE", error: null }))} /><span>{currency}</span></div>
            {recharge.amount && !amountValid && <p className="ql-recharge-error" role="alert">Saisissez un montant entier entre 100 et 2 500 000 {currency}.</p>}
            <div className="ql-recharge-actions">
              <button type="button" className="btn btn-primary" disabled={!amountValid || Boolean(recharge.rechargeId) || RECHARGE_ACTIVE_STATES.has(recharge.status)} onClick={() => setRecharge((current) => ({ ...current, confirming: true }))}>Continuer</button>
              {!recharge.rechargeId && !RECHARGE_ACTIVE_STATES.has(recharge.status) && <button type="button" className="btn btn-outline-secondary" onClick={() => setRecharge((current) => ({ ...current, open: false, amount: "", error: null }))}>Annuler</button>}
              {recharge.rechargeId && <button type="button" className="btn btn-outline-primary" disabled={recharge.checking} onClick={refreshRechargeStatus}>{recharge.checking ? "Vérification…" : "Actualiser le statut"}</button>}
              {recharge.status === "UNCERTAIN" && !recharge.rechargeId && <button type="button" className="btn btn-outline-secondary" onClick={abandonOrphanedRecharge}>Abandonner cette tentative</button>}
            </div>
            {RECHARGE_ACTIVE_STATES.has(recharge.status) && recharge.rechargeId && <p className="ql-recharge-pending" role="status">Recharge en attente de confirmation CinetPay.</p>}
            {recharge.error && <p className="ql-recharge-error" role="alert">{recharge.error}</p>}
          </div>
        )}
        {recharge.open && recharge.confirming && (
          <div className="ql-wallet-confirmation" role="dialog" aria-modal="true" aria-labelledby="wallet-recharge-confirmation">
            <h3 id="wallet-recharge-confirmation">Confirmer la recharge de {formatAmount(amountNumber, currency)} ?</h3>
            <div><button type="button" className="btn btn-primary" onClick={initializeRecharge}>Confirmer</button><button type="button" className="btn btn-outline-secondary" onClick={() => setRecharge((current) => ({ ...current, confirming: false }))}>Annuler</button></div>
          </div>
        )}
      </section>

      <section className="ql-wallet-panel">
        <div className="ql-wallet-panel-heading">
          <div><span className="ql-teacher-eyebrow">Mouvements</span><h2>Historique des transactions</h2></div>
          <div className="ql-wallet-filters" aria-label="Filtrer les transactions">
            {[['', 'Toutes'], ['payment', 'Paiements'], ['recharge', 'Recharges']].map(([value, label]) => (
              <button type="button" key={label} className={type === value ? "active" : ""} onClick={() => chooseType(value)}>{label}</button>
            ))}
          </div>
        </div>

        {loading && <div className="ql-wallet-state" role="status"><span className="spinner-border text-primary" /> Chargement…</div>}
        {!loading && error && <div className="ql-wallet-state error" role="alert"><span>{error}</span><button type="button" onClick={load}>Réessayer</button></div>}
        {!loading && !error && history.results.length === 0 && <div className="ql-wallet-empty"><i className="bi bi-receipt" /><h3>Aucune transaction</h3><p>Les paiements et recharges apparaîtront ici.</p></div>}
        {!loading && !error && history.results.length > 0 && (
          <div className="ql-wallet-transactions">
            {history.results.map((entry) => (
              <article key={entry.id}>
                <i className={`bi ${entry.type === "recharge" ? "bi-plus-circle" : "bi-receipt"}`} aria-hidden="true" />
                <div className="ql-wallet-transaction-main">
                  <strong>{entry.description || typeLabels[entry.type] || "Transaction"}</strong>
                  <span>{formatDate(entry.created_at)}{entry.reference ? ` · ${entry.reference}` : ""}</span>
                  {entry.exam_id && <small>Épreuve #{entry.exam_id}</small>}
                  {entry.bonus_amount > 0 && <small className="ql-wallet-bonus">+ {formatAmount(entry.bonus_amount, currency)} bonus de bienvenue</small>}
                </div>
                <div className="ql-wallet-transaction-value">
                  <strong className={entry.type}>{entry.type === "recharge" ? "+" : "−"}{formatAmount(entry.amount, currency)}</strong>
                  <span className={`ql-wallet-status ${entry.status}`}>{statusLabels[entry.status] || entry.status}</span>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && !error && history.totalPages > 1 && (
          <nav className="ql-exam-pagination" aria-label="Pagination du portefeuille">
            <button type="button" className="btn btn-outline-primary" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Précédent</button>
            <span>Page {history.page} / {history.totalPages}</span>
            <button type="button" className="btn btn-outline-primary" disabled={!history.next && page >= history.totalPages} onClick={() => setPage((value) => value + 1)}>Suivant</button>
          </nav>
        )}
      </section>
    </main>
  );
}
